import axios, { AxiosError, isAxiosError } from 'axios'
import { ApplicationError, ErrorType } from 'core'

// TODO: Get the value from .env variables or config file
const API_BASE_URL = 'https://apiprod.codigohub.dev'
const TIME_OUT = 60000

export enum HTTPMethods {
	Get = 'GET',
	Post = 'POST',
	Put = 'PUT',
	Patch = 'PATCH',
	Delete = 'DELETE'
}

export interface HTTPRequestConfig<M> {
	baseURL?: string
	method?: HTTPMethods
	data?: M
	params?: unknown
	headers?: Record<string, string>
	onUploadProgress?: (sent: number, total: number) => void
}

interface HTTPErrors {
	code?: string
	error?: string
}

export class HTTPClient {
	private handleHTTPErrors(error: unknown) {
		if (isAxiosError(error) && error.response) {
			if (error.response.data as HTTPErrors) {
				switch (error.response.status) {
					case 400:
						return new ApplicationError({
							type: ErrorType.BadRequest,
							message: error.response.data.error,
							code: error.response.data.code
						})
					case 401:
						return new ApplicationError({
							type: ErrorType.Unauthorized,
							message: error.response.data.error,
							code: error.response.data.code
						})
					case 403:
						/**
						 * `/me` is the first endpoint that requires authentication
						 * If the endpoint is not `/me`
						 * redirect the user to the login page. This is necessary because:
						 * 1. Users can log out in one tab.
						 * 2. Actions in other tabs might still be in progress.
						 * 3. These actions will fail with a 403 after logout.
						 * 4. We need to redirect to the login page to re-authenticate.
						 */
						if (
							this.areCredentialsInvalid(error) &&
							error.response.config.url !== '/authv2/home/api/v1/me'
						) {
							window.location.replace('/')
							return
						}
						return new ApplicationError({
							type: ErrorType.Forbidden,
							message: error.response.data.error,
							code: error.response.data.code
						})
					case 404:
						return new ApplicationError({
							type: ErrorType.NotFound,
							message: error.response.data.error,
							code: error.response.data.code
						})
					case 409:
						return new ApplicationError({
							type: ErrorType.Conflict,
							message: error.response.data.error,
							code: error.response.data.code
						})
					case 500:
						return new ApplicationError({
							type: ErrorType.ServerError,
							message: error.response.data.error,
							code: error.response.data.code
						})

					default:
						return new ApplicationError({
							type: ErrorType.Unknown,
							message: 'Server Internal Error',
							code: 'unknown'
						})
				}
			}

			if ((error.response.data as { message: string }).message) {
				return new ApplicationError({
					type: ErrorType.ServerError,
					message: error.response.data.message
				})
			}
		}

		return new ApplicationError({
			type: ErrorType.Unknown,
			code: 'unknown',
			message: 'Server Internal Error'
		})
	}

	private areCredentialsInvalid(error: AxiosError) {
		return (
			error.response?.status === 403 &&
			error.response?.data === 'RBAC: access denied'
		)
	}

	async fetch<T, M = undefined>(
		endpoint: string,
		requestConfig?: HTTPRequestConfig<M>
	): Promise<{ data?: T; error?: ApplicationError; status?: number }> {
		const request = {
			method: HTTPMethods.Get,
			baseURL: API_BASE_URL,
			...requestConfig
		}

		try {
			const response = await axios.request<{ data: T }>({
				withCredentials: true,
				baseURL: request.baseURL,
				data: request.data,
				method: request.method,
				params: request.params,
				timeout: TIME_OUT,
				url: endpoint,
				headers: {
					'Content-Type': 'application/json; charset=utf-8',
					...request.headers
				},
				onUploadProgress: progress => {
					request.onUploadProgress?.call(
						this,
						progress.loaded || 0,
						progress.total || 0
					)
				}
			})

			return {
				data: response.data.data,
				status: response.status
			}
		} catch (error) {
			if (isAxiosError(error)) {
				return {
					error: this.handleHTTPErrors(error),
					status: error.response?.status
				}
			}
			return { error: this.handleHTTPErrors(error) }
		}
	}
}
